// src/components/CompanyModal.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import { useData } from '../context/DataContext';
import styles from './CompanyModal.module.css';

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE_MB = 5;
const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const internalLogoPath = '/company/api/logo'; // Define path constant

const CompanyModal = () => {
    const {
        // ... context values
        isCompanyModalOpen,
        setIsCompanyModalOpen,
        companyData,
        fetchCompanyData,
        updateCompanyData,
        isCompanyModalLoading,
        companyModalError,
        setCompanyModalError,
    } = useData();

    // State
    const [currentCompanyName, setCurrentCompanyName] = useState('');
    const [currentCompanyDescription, setCurrentCompanyDescription] = useState('');
    const [currentCompanyPolicy, setCurrentCompanyPolicy] = useState('');
    const [logoInputMode, setLogoInputMode] = useState('url');
    const [currentLogoUrl, setCurrentLogoUrl] = useState(''); // Value for URL input
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(''); // Local blob: URL for file preview
    const [logoPreviewSrc, setLogoPreviewSrc] = useState(''); // Final src for the <img> tag
    const [logoError, setLogoError] = useState(false);
    const [clearExistingLogo, setClearExistingLogo] = useState(false);
    const [imageLoadKey, setImageLoadKey] = useState(Date.now()); // State to force image re-render
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const fileInputRef = useRef(null);

    // Fetch data when modal opens
    useEffect(() => {
        if (isCompanyModalOpen) {
            console.log("[CompanyModal] Opened, fetching data...");
            setCompanyModalError(null);
            setLogoError(false);
            setSelectedFile(null);
            if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) { // Clean up previous blob URL if exists
                 URL.revokeObjectURL(filePreviewUrl);
            }
            setFilePreviewUrl('');
            setClearExistingLogo(false);
            setLogoPreviewSrc(''); // Clear preview initially
            fetchCompanyData();
        }
    }, [isCompanyModalOpen, fetchCompanyData, setCompanyModalError]); // Removed filePreviewUrl


    // Primary Effect: Update form fields AND determine initial logo source from fetched data
    useEffect(() => {
        console.log("[CompanyModal] CompanyData effect triggered. Data:", companyData);
        if (companyData) {
            // Update text fields
            setCurrentCompanyName(companyData.companyName || '');
            setCurrentCompanyDescription(companyData.companyDescription || '');
            setCurrentCompanyPolicy(companyData.companyPolicy || '');
            // Set URL input field value (even if blob exists, show the original URL if available)
            setCurrentLogoUrl(companyData.dbLogoURL || '');

            // --- Determine the source for the image preview ---
            let initialPreviewSrc = '';
            if (companyData.logoUrlToDisplay) {
                if (companyData.logoUrlToDisplay.startsWith(internalLogoPath)) {
                    // It's an internal URL, construct the absolute path
                    initialPreviewSrc = `${backendBaseUrl}${companyData.logoUrlToDisplay}`;
                    console.log("[CompanyModal] Determined initial internal preview source:", initialPreviewSrc);
                } else {
                    // It's an external URL
                    initialPreviewSrc = companyData.logoUrlToDisplay;
                     console.log("[CompanyModal] Determined initial external preview source:", initialPreviewSrc);
                }
                setLogoInputMode('url'); // Default display mode to URL if *any* logo exists
            } else {
                 console.log("[CompanyModal] No logoUrlToDisplay in fetched data.");
                 setLogoInputMode('url'); // Default to URL mode if no logo
            }

            setLogoPreviewSrc(initialPreviewSrc); // Set the determined source
            setImageLoadKey(Date.now()); // *** Force img re-render with a new key ***

            // Reset interactive states
            setLogoError(false);
            setSelectedFile(null);
            if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
                 URL.revokeObjectURL(filePreviewUrl); // Clean up previous session blob URL if exists
            }
            setFilePreviewUrl('');
            setClearExistingLogo(false);

        } else {
            // Reset form if no data
             console.log("[CompanyModal] No data/cleared, resetting form.");
             setCurrentCompanyName('');
             setCurrentCompanyDescription('');
             setCurrentCompanyPolicy('');
             setCurrentLogoUrl('');
             setLogoPreviewSrc('');
             setLogoInputMode('url');
             setLogoError(false);
             setSelectedFile(null);
             if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
                 URL.revokeObjectURL(filePreviewUrl);
             }
             setFilePreviewUrl('');
             setClearExistingLogo(false);
             setImageLoadKey(Date.now()); // Update key even on clear
        }
    // Only depend on companyData for this effect
    }, [companyData]); // Removed backendBaseUrl - it's a constant


    // Secondary Effect: Update preview based *only* on user interaction (mode change, file select, URL input)
    useEffect(() => {
        console.log("[CompanyModal] Interaction effect triggered. Mode:", logoInputMode, "File:", !!selectedFile, "URL:", currentLogoUrl, "Clear:", clearExistingLogo);
        let interactionPreviewSrc = ''; // Source determined by interaction
        if (clearExistingLogo) {
            interactionPreviewSrc = ''; // Explicit clear overrides everything
        } else if (logoInputMode === 'file' && filePreviewUrl) {
            interactionPreviewSrc = filePreviewUrl; // Use local blob preview
        } else if (logoInputMode === 'url') {
            interactionPreviewSrc = currentLogoUrl; // Use value from URL input
        }
        // Note: No fallback to companyData here. This effect *only* reflects direct user input.

        setLogoPreviewSrc(interactionPreviewSrc);
        setLogoError(false); // Reset logo error state on interaction changes
        setImageLoadKey(Date.now()); // Update key on interaction changes too

    }, [logoInputMode, filePreviewUrl, selectedFile, currentLogoUrl, clearExistingLogo]);


    // Cleanup object URLs
    useEffect(() => {
        // This effect should ONLY run when filePreviewUrl changes to revoke the *previous* URL
        const currentBlobUrl = filePreviewUrl; // Capture value for cleanup function
        return () => {
            if (currentBlobUrl && currentBlobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentBlobUrl);
                console.log("[CompanyModal] Revoked object URL:", currentBlobUrl);
            }
        };
    }, [filePreviewUrl]); // Depend only on filePreviewUrl

    // --- Handlers ---

    const handleCloseRequest = useCallback(() => {
            if (isCompanyModalLoading) return;
            setIsCompanyModalOpen(false);
        }, [isCompanyModalLoading, setIsCompanyModalOpen]);

    const handleModeChange = (event) => {
        const newMode = event.target.value;
        console.log("[CompanyModal] Mode changed to:", newMode);
        setLogoInputMode(newMode);
        // Don't reset file/URL input values when just switching modes
        // The preview will update based on the mode in the interaction effect
        setClearExistingLogo(false); // Changing mode means we are not clearing
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        // Revoke previous blob URL before setting a new one
        if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
             URL.revokeObjectURL(filePreviewUrl);
             console.log("[CompanyModal] Revoked previous object URL on new file selection:", filePreviewUrl);
             setFilePreviewUrl(''); // Clear state first
        }

        if (!file) {
            setSelectedFile(null);
            // setFilePreviewUrl(''); // Already cleared above
            setClearExistingLogo(false);
            return;
        }
        // ... (Validation logic as before) ...
         if (!ALLOWED_MIME_TYPES.includes(file.type)) {
             setLogoError(`Invalid file type. Allowed: ${ALLOWED_MIME_TYPES.join(', ')}`);
             setSelectedFile(null);
             if (fileInputRef.current) fileInputRef.current.value = '';
             setClearExistingLogo(false);
             return;
         }
         if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
             setLogoError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
             setSelectedFile(null);
             if (fileInputRef.current) fileInputRef.current.value = '';
             setClearExistingLogo(false);
             return;
         }


        setSelectedFile(file);
        setLogoError(false);
        setClearExistingLogo(false);

        // Generate new preview URL
        const newPreviewUrl = URL.createObjectURL(file);
        setFilePreviewUrl(newPreviewUrl); // This will trigger the interaction useEffect
        console.log("[CompanyModal] Created object URL:", newPreviewUrl);
    };

     const handleClearLogo = () => {
         console.log("[CompanyModal] Clear logo clicked.");
         // Revoke existing blob URL if one exists
         if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(filePreviewUrl);
              console.log("[CompanyModal] Revoked object URL on clear:", filePreviewUrl);
         }
         setClearExistingLogo(true); // Set clear flag (triggers interaction effect)
         setSelectedFile(null);
         setFilePreviewUrl('');
         setCurrentLogoUrl(''); // Also clear URL input field
         if (fileInputRef.current) fileInputRef.current.value = '';
         setLogoError(false);
         // Let the interaction effect handle setting logoPreviewSrc to ''
     };


    // ... (handleSave, handleCloseRequest, handleLogoPreviewError remain largely the same) ...
     const handleSave = async () => {
         // ... (validation, payload construction using FormData or JSON based on mode/file/clear flag) ...
         // Crucially, the payload construction logic based on final state should be correct from previous step
         if (!currentCompanyName.trim()) {
             setCompanyModalError("Company name cannot be empty.");
             return;
         }
         // Allow saving even if there was a preview error, unless explicitly clearing didn't work?
         // if (logoError && !clearExistingLogo) {
         //     setCompanyModalError(`Cannot save due to logo error: ${logoError}`);
         //     // return; // Decided previously to allow saving text fields
         // }

         let payload;
         const commonData = { /* ... */
             companyName: currentCompanyName.trim(),
             companyDescription: currentCompanyDescription.trim(),
             companyPolicy: currentCompanyPolicy.trim(),
          };

         if (logoInputMode === 'file' && selectedFile) {
             console.log("[CompanyModal] Saving in FILE mode.");
             payload = new FormData();
             payload.append('companyName', commonData.companyName);
             payload.append('companyDescription', commonData.companyDescription);
             payload.append('companyPolicy', commonData.companyPolicy);
             payload.append('logoFile', selectedFile);
         } else if (clearExistingLogo) {
              console.log("[CompanyModal] Saving with explicit CLEAR LOGO flag.");
              payload = new FormData();
              payload.append('companyName', commonData.companyName);
              payload.append('companyDescription', commonData.companyDescription);
              payload.append('companyPolicy', commonData.companyPolicy);
              payload.append('clearLogo', 'true');
         } else {
             console.log("[CompanyModal] Saving in URL mode (or text only).");
             payload = {
                 ...commonData,
                 logoURL: currentLogoUrl.trim() || null,
             };
         }

         console.log("[CompanyModal] Attempting to save company data. Payload:", payload);
         const result = await updateCompanyData(payload);

         if (result?.status) {
             console.log("[CompanyModal] Save successful.");
             // DataContext re-fetches, which triggers the companyData useEffect to update UI
             // Reset interactive states after successful save
             setSelectedFile(null);
             if (fileInputRef.current) fileInputRef.current.value = '';
             // Don't reset clearExistingLogo here, let the fetch handle it
             setShowSuccessMessage(true); // Implement success message if desired
             setTimeout(() => setShowSuccessMessage(false), 3000);
         } else {
             console.error("[CompanyModal] Save failed:", result?.message);
             // Error is set in context
         }
     };

     const handleLogoPreviewError = () => {
         if (logoPreviewSrc) {
             console.warn("[CompanyModal] Failed to load logo image preview from:", logoPreviewSrc);
             setLogoError(true); // Set error state
         } else {
             setLogoError(false);
         }
     };

    return (
        <Modal
            isOpen={isCompanyModalOpen}
            onRequestClose={handleCloseRequest}
            className={`${styles.modalContent} ${isCompanyModalLoading ? styles.loading : ''}`}
            overlayClassName={styles.modalOverlay}
            contentLabel="Manage Company Details"
            shouldCloseOnOverlayClick={!isCompanyModalLoading}
            shouldCloseOnEsc={!isCompanyModalLoading}
            aria={{ modal: 'true' }}
        >
            {isCompanyModalLoading && <div className={styles.spinnerOverlay}><div className={styles.spinner}></div></div>}
            <h2 id="companyModalTitle">Manage Company Details</h2>

            {companyModalError && (
                <div className={styles.modalError}>Error: {companyModalError}</div>
            )}
            {/* Display local logo validation error separately */}
             {logoError && !companyModalError && (
                 <div className={styles.modalError} style={{ backgroundColor: '#6c500f' }}>Logo Issue: {logoError}</div>
             )}

            <div className={styles.modalBody}>
                {/* Left Panel: Details & Logo */}
                <div className={styles.leftPanel}>
                    <div className={styles.formGroup}>
                        <label htmlFor="companyNameInput">Company Name:</label>
                        <input
                            type="text"
                            id="companyNameInput"
                            value={currentCompanyName}
                            onChange={(e) => setCurrentCompanyName(e.target.value)}
                            placeholder="Enter company name"
                            disabled={isCompanyModalLoading}
                            required
                            aria-required="true"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="companyDescriptionInput">Company Description:</label>
                        <textarea
                            id="companyDescriptionInput"
                            value={currentCompanyDescription}
                            onChange={(e) => setCurrentCompanyDescription(e.target.value)}
                            rows="4" // Adjusted rows
                            placeholder="Enter short company description (optional)"
                            disabled={isCompanyModalLoading}
                        />
                    </div>
                    {/* --- Logo Input Section --- */}
                    <fieldset className={styles.logoFieldset}>
                        <legend>Company Logo</legend>
                        {/* Mode Selection */}
                        <div className={styles.radioGroup}>
                            <label>
                                <input
                                    type="radio"
                                    name="logoMode"
                                    value="url"
                                    checked={logoInputMode === 'url'}
                                    onChange={handleModeChange}
                                    disabled={isCompanyModalLoading}
                                />
                                Enter URL
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="logoMode"
                                    value="file"
                                    checked={logoInputMode === 'file'}
                                    onChange={handleModeChange}
                                    disabled={isCompanyModalLoading}
                                />
                                Upload File
                            </label>
                        </div>

                        {/* Conditional Inputs */}
                        {logoInputMode === 'url' && (
                            <div className={styles.formGroup} style={{ marginTop: '10px' }}>
                                <label htmlFor="companyLogoUrlInput">Logo URL:</label>
                                <input
                                    type="text"
                                    id="companyLogoUrlInput"
                                    value={currentLogoUrl}
                                    onChange={(e) => setCurrentLogoUrl(e.target.value)}
                                    placeholder="https://example.com/logo.png"
                                    disabled={isCompanyModalLoading}
                                />
                            </div>
                        )}

                        {logoInputMode === 'file' && (
                            <div className={styles.formGroup} style={{ marginTop: '10px' }}>
                                <label htmlFor="companyLogoFileInput">Select Logo File:</label>
                                <input
                                    type="file"
                                    id="companyLogoFileInput"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/png, image/jpeg, image/gif, image/webp" // Specify allowed types
                                    disabled={isCompanyModalLoading}
                                    className={styles.fileInput}
                                />
                                <span className={styles.fileInputHint}>Max {MAX_FILE_SIZE_MB}MB. Allowed: png, jpg, gif, webp</span>
                            </div>
                        )}
                         {/* Clear Button */}
                         <button
                              type="button"
                              onClick={handleClearLogo}
                              className={styles.clearButton}
                              disabled={isCompanyModalLoading || (!selectedFile && !currentLogoUrl && !companyData?.logoUrlToDisplay)}
                              title="Remove current logo"
                          >
                              Clear Logo
                          </button>

                    </fieldset>
                     {/* --- End Logo Input Section --- */}


                    {/* Logo Preview Area */}
                    <fieldset className={styles.logoPreviewArea}>
                        <legend>Logo Preview</legend>
                        {logoPreviewSrc ? (
                            <img
                                // Add a key based on the src to force re-render on src change
                                key={imageLoadKey}
                                src={logoPreviewSrc}
                                alt="Company Logo Preview"
                                className={styles.logoPreviewImage}
                                onError={handleLogoPreviewError}
                            />
                        ) : (
                            <div className={styles.logoPlaceholder}>
                                {logoError ? "Error loading/displaying logo" : "No logo provided or selected"}
                            </div>
                        )}
                    </fieldset>
                </div> {/* End Left Panel */}

                {/* Right Panel: Policy */}
                <div className={styles.rightPanel}>
                    <div className={`${styles.formGroup} ${styles.policyGroup}`}>
                        <label htmlFor="companyPolicyInput">Company Policy:</label>
                        <textarea
                            id="companyPolicyInput"
                            value={currentCompanyPolicy}
                            onChange={(e) => setCurrentCompanyPolicy(e.target.value)}
                            rows="15" // Takes up more vertical space
                            placeholder="Enter company policy text (optional)"
                            disabled={isCompanyModalLoading}
                            className={styles.policyTextarea} // Apply specific class if needed
                        />
                    </div>
                </div>
            </div>
            {showSuccessMessage && (
            <div className={styles.modalSuccess}> {/* Add styles for this class */}
                ✓ Company data saved successfully!
            </div>
            )}
            {/* Bottom: Action Buttons */}
            <div className={styles.modalActions}>
                <button
                    onClick={handleSave}
                    className={`${styles.actionButton} ${styles.saveButton}`}
                    disabled={isCompanyModalLoading || !currentCompanyName.trim()} // Disable save if loading or name is empty
                >
                    {isCompanyModalLoading ? 'Saving...' : 'Save Changes'}
                </button>

                <button
                    onClick={handleCloseRequest}
                    className={`${styles.actionButton} ${styles.closeButton}`}
                    disabled={isCompanyModalLoading} // Disable close if loading
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default CompanyModal;